import { ThemeSchemaResponse } from '@api';
import { FollowThemeButton } from '@terminal/components/follow-theme-button/FollowThemeButton';
import { useCatalystInsightsList } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { CatalystInsightPill } from '@terminal/widgets/catalyst-insights-drawer/catalyst-insight-pill/CatalystInsightPill';
import clsx from 'clsx';
import { startOfDay, subDays } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeLogoIcon } from '../../../../views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import styles from './ThemeHeader.module.scss';

interface ThemeHeaderProps {
  theme: ThemeSchemaResponse;
  isCondensed?: boolean;
}

export const ThemeHeader = ({
  theme,
  isCondensed = false,
}: ThemeHeaderProps) => {
  const { t } = useTranslation(['widget']);
  const isMacroFinancialThemeEnabled = useFlags(state =>
    state.getFeatureFlagState('KG-786-Macro_Financial_Theme')
  );

  const { id, name, logo_url: logoUrl, type } = theme;
  const fromDate = useMemo(() => startOfDay(subDays(new Date(), 3)), []);
  const { data: catalystInsightsData } = useCatalystInsightsList({
    themes: [id],
    categories: ['market_wide'],
    from: fromDate.toISOString(),
    page: 1,
    page_size: 1,
  });
  const isMSTheme = theme?.metadata?.ms_basket_ticker;

  return (
    <div className={styles.flexRow}>
      <div
        className={clsx(styles.logoAndTitle, {
          [styles.condensed]: isCondensed,
        })}
      >
        <ThemeLogoIcon logo_url={logoUrl} name={name} key={id} type={type} />
        <div className={styles.flexColumn}>
          <span
            className={clsx(styles.themeType, {
              [styles.macro]: isMacroFinancialThemeEnabled && type === 'macro',
              [styles.financial]:
                isMacroFinancialThemeEnabled && type === 'financial',
            })}
          >
            {/* i18next-extract-mark-context-next-line ["industry", "macro", "financial"]  */}
            {isMacroFinancialThemeEnabled &&
              !isMSTheme &&
              t('widget:themeOverview.type', {
                context: type,
              })}
          </span>
          <span
            className={clsx(styles.titleText, {
              [styles.condensed]: isCondensed,
            })}
          >
            {name}
          </span>
        </div>
      </div>
      <div className={styles.rightCluster}>
        {!!catalystInsightsData?.length && (
          <CatalystInsightPill
            insightId={catalystInsightsData[0].id}
            themeId={id}
            subCategory={catalystInsightsData[0].sub_category}
            isClickable
            source={POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW}
            category={catalystInsightsData[0].category}
          />
        )}
        <FollowThemeButton themeId={id} />
      </div>
    </div>
  );
};
